import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiError } from '@/api/client';
import { fetchFavorites, postFavorites } from '@/api/plan';
import { tripKeys } from '@/api/queryKeys';

/**
 * Persistente Favoriten pro Trip+Session. Backend speichert die komplette Liste,
 * Client schickt sie bei jedem Toggle als Vollbestand (kein Diff). Optimistic
 * Update sorgt für sofortiges UI-Feedback.
 *
 * Hinweis: Der alte vanilla-Client cachte Favoriten zusätzlich in localStorage
 * (`fav_<slug>`) als Offline-Fallback. Für die SPA verzichten wir auf den Doppel-
 * Cache — TanStack Query hält die Liste im Memory und re-fetcht bei Reconnect.
 */
export function useFavorites(slug: string) {
  const qc = useQueryClient();

  const favsQuery = useQuery({
    queryKey: tripKeys.favorites(slug),
    queryFn: ({ signal }) => fetchFavorites(slug, signal).then((r) => r.favorites),
    staleTime: 5 * 60_000,
  });

  const toggleMutation = useMutation<string[], ApiError, { actId: string; want: boolean }>({
    mutationFn: async ({ actId, want }) => {
      const current = qc.getQueryData<string[]>(tripKeys.favorites(slug)) ?? [];
      const set = new Set(current);
      if (want) set.add(actId); else set.delete(actId);
      const next = Array.from(set);
      const res = await postFavorites(slug, next);
      return res.favorites;
    },
    onMutate: async ({ actId, want }) => {
      await qc.cancelQueries({ queryKey: tripKeys.favorites(slug) });
      const previous = qc.getQueryData<string[]>(tripKeys.favorites(slug)) ?? [];
      const set = new Set(previous);
      if (want) set.add(actId); else set.delete(actId);
      qc.setQueryData<string[]>(tripKeys.favorites(slug), Array.from(set));
      return { previous };
    },
    onError: (_err, _input, context) => {
      const ctx = context as { previous?: string[] } | undefined;
      if (ctx?.previous) qc.setQueryData(tripKeys.favorites(slug), ctx.previous);
    },
    onSuccess: (favorites) => {
      qc.setQueryData<string[]>(tripKeys.favorites(slug), favorites);
    },
  });

  const favs = favsQuery.data ?? [];

  return {
    favorites: favs,
    favoritesSet: new Set(favs),
    isLoading: favsQuery.isLoading,
    toggle: (actId: string, want: boolean) => toggleMutation.mutate({ actId, want }),
    isToggling: toggleMutation.isPending,
  };
}
